import { useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import { cn } from '@/utils/cn';
import { useUIStore } from '@/stores/uiStore';
import { router } from '@/routes';
import { BrandProvider } from '@/components/BrandProvider';

// ──────────────────────────────────────────────
// TanStack Query client — sensible defaults
// ──────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 30, // 30 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

// ──────────────────────────────────────────────
// Offline indicator
// ──────────────────────────────────────────────

function OfflineIndicator() {
  const isOnline = useUIStore((s) => s.isOnline);

  if (isOnline) return null;

  return (
    <div
      className={cn(
        'fixed bottom-4 left-1/2 z-[100] -translate-x-1/2',
        'flex items-center gap-2 rounded-full bg-slate-900 px-4 py-2 shadow-lg',
        'animate-in slide-in-from-bottom-4 fade-in duration-300'
      )}
      role="status"
      aria-live="polite"
    >
      <span className="relative flex h-2.5 w-2.5">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-amber-500" />
      </span>
      <span className="text-sm font-medium text-white">Sin conexion</span>
    </div>
  );
}

// ──────────────────────────────────────────────
// App — Root component
// ──────────────────────────────────────────────

function App() {
  const setOnline = useUIStore((s) => s.setOnline);

  // Listen for online / offline events
  useEffect(() => {
    function handleOnline() {
      setOnline(true);
    }
    function handleOffline() {
      setOnline(false);
    }

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Set initial state
    setOnline(navigator.onLine);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [setOnline]);

  return (
    <QueryClientProvider client={queryClient}>
      <BrandProvider>
        <RouterProvider router={router} />

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#fff',
            color: '#0f172a',
            fontSize: '14px',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          },
          success: {
            iconTheme: {
              primary: '#10b981',
              secondary: '#fff',
            },
          },
          error: {
            iconTheme: {
              primary: '#ef4444',
              secondary: '#fff',
            },
            duration: 5000,
          },
        }}
      />

      {/* Online/offline indicator */}
      <OfflineIndicator />
      </BrandProvider>
    </QueryClientProvider>
  );
}

export default App;
